/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading */
import { useEffect, useImperativeHandle, useState, forwardRef } from 'react';
import { format, parse } from 'date-fns';
import { UseFormReturn } from 'react-hook-form';
import { toast } from 'react-toastify';

import { BookedSeat, Cinema, Schedule } from '@app/types';
import { currency, currencycents } from '@app/utils';
import { firebase } from '@app/config/fire-conf';
import TicketSelect from '@app/components/ticket-select';

import styles from './movie-tickets.module.css';

const dateFormat = 'dd-MM-yyyy';
// const dateFormat = 'dd/MM/yyyy - iiii';
const timeFormat = 'h:mm a';

export type Tickets = {
  adults: number;
  children: number;
};

export type WizardStepMovieTicketsProps = {
  adults: string;
  children: string;
  childTicketPrice: number;
  adultTicketPrice: number;
  childTicketPriceTotal: number;
  adultTicketPriceTotal: number;
  adultConvenienceFee: number;
  childConvenienceFee: number;
  ticketsCount: number;
  currentSelectionIds: string;
  discount: number;
  amountPaid: number;
  childTotalTax: number;
  adultTotalTax: number;
};

export type MovieTicketsHandle = {
  reset: () => void;
};

type MovieTicketsProps = {
  scheduleId?: string;
  cinema?: Cinema;
  loading: boolean;
  onlineFee: number;
  useFormReturn: Omit<UseFormReturn<WizardStepMovieTicketsProps>, 'trigger' | 'handleSubmit'>;
};

const MovieTickets: React.ForwardRefRenderFunction<MovieTicketsHandle, MovieTicketsProps> = (
  { scheduleId, cinema, loading = false, onlineFee, useFormReturn }: MovieTicketsProps,
  forwardedRef,
): JSX.Element => {
  const [ticketCount, setTicketCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [schedule, setSchedule] = useState<Schedule>();
  const [currentSelectionIds, setCurrentSelectionIds] = useState<number[]>([]);
  const [bookings, setBookings] = useState<BookedSeat[]>([]);
  const [totalVat, setTotalVat] = useState<number>(0);
  const {
    register,
    setValue,
    getValues,
    formState: { touchedFields },
  } = useFormReturn;

  setValue('adultConvenienceFee', onlineFee);
  setValue('childConvenienceFee', onlineFee);

  const { onChange: onAdultChange, ...restAdultRegister } = register('adults', { required: true });
  const adultTicketPriceRegister = register('adultTicketPrice');
  const adultTicketPriceTotalRegister = register('adultTicketPriceTotal');
  const { onChange: onChildrenChange, ...restChildrenRegister } = register('children', {
    required: true,
  });
  const childTicketPriceRegister = register('childTicketPrice');
  const childTicketPriceTotalRegister = register('childTicketPriceTotal');
  const adultConvenienceFeeRegister = register('adultConvenienceFee');
  const childConvenienceFeeRegister = register('childConvenienceFee');

  const [vat, setVat] = useState<number>(0);
  const [entertainmentTax, setEntertainmentTax] = useState<number>(0);
  const [levy, setLevy] = useState<number>(0);

  const getVatPercentage = async () => {
    const vatSnapshot = await firebase.firestore().collection('settings').doc('taxes').get();
    const feeData1 = vatSnapshot.data() as { entertainmentTax: number };
    const feeData2 = vatSnapshot.data() as { vat: number };
    setEntertainmentTax(feeData1.entertainmentTax);
    setVat(feeData2.vat);
  };

  const getLevyValue = async () => {
    const vatSnapshot = await firebase.firestore().collection('settings').doc('otherLevy').get();
    const levyData = vatSnapshot.data() as { levyOnTicket: number };
    setLevy(levyData.levyOnTicket);
  };

  useEffect(() => {
    getVatPercentage();
    getLevyValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(forwardedRef, () => ({
    reset() {
      setTicketCount(0);
      setCurrentSelectionIds([]);
    },
  }));

  const toggleSelect = (seatId: number) => {
    // if (ticketCount > 8) {
    //   toast('Maximum up to 8 tickets can be selected', {
    //     type: 'error',
    //   });
    // } else {
    const toggleSeat = bookings.find((item) => item.id === seatId);
    if (toggleSeat) {
      if (toggleSeat.status === 'available') {
        if (ticketCount > 7) {
          toast('Maximum up to 8 tickets can be selected', {
            type: 'error',
          });
        } else {
          setTicketCount((currentCount) => currentCount + 1);
          toggleSeat.status = 'selected';
          setCurrentSelectionIds([...currentSelectionIds, toggleSeat.id]);
        }
      } else if (toggleSeat.status === 'selected') {
        toggleSeat.status = 'available';
        setTicketCount((currentCount) => currentCount - 1);
        const updated = currentSelectionIds.filter((selection) => selection !== seatId);
        setCurrentSelectionIds([...updated]);
      }

      const selectedItems = bookings.filter((item) => item.status === 'selected');
      // setTicketCount(selectedItems.length);
      if (!touchedFields.children) {
        if (schedule) {
          setValue('adults', selectedItems.length.toString());
          setValue(
            'adultTicketPriceTotal',
            selectedItems.length * (schedule.showtime.ticket.adult + onlineFee),
          );
          // setValue(
          //   'adultTotalTax',
          //   selectedItems.length *
          //     ((((schedule.showtime.ticket.adult + levy) * 100 + 2 * levy * entertainmentTax) *
          //       vat) /
          //       ((100 + entertainmentTax) * (100 + vat))),
          // );
          // setValue(
          //   'adultTotalTax',
          //   (selectedItems.length * schedule.showtime.ticket.adult * vat) / (100 + vat),
          // );
          setValue(
            'adultTotalTax',
            selectedItems.length *
              (((schedule.showtime.ticket.adult -
                levy -
                ((schedule.showtime.ticket.adult - levy) / (100 + entertainmentTax)) *
                  entertainmentTax) /
                (100 + vat)) *
                vat),
          );
          setValue('adultConvenienceFee', selectedItems.length * onlineFee);
        }
      }
      const adultsTotal = getValues('adultTicketPriceTotal');
      const childTotal = getValues('childTicketPriceTotal');
      const childTotalTax = getValues('childTotalTax');
      const adultTotalTax = getValues('adultTotalTax');
      setTotalVat(childTotalTax + adultTotalTax);
      setTotal(adultsTotal + childTotal);
      setValue('amountPaid', adultsTotal + childTotal);
      setBookings([...bookings]);
    }
    // }
  };

  const onInputChange = (value: number, fieldName: string) => {
    // calculate Total
    if (schedule) {
      if (fieldName === 'children') {
        setValue('childTicketPriceTotal', value * (schedule.showtime.ticket.child + onlineFee));
        // setValue(
        //   'childTotalTax',
        //   value *
        //     ((((schedule.showtime.ticket.child + levy) * 100 + 2 * levy * entertainmentTax) * vat) /
        //       ((100 + entertainmentTax) * (100 + vat))),
        // );
        // setValue('childTotalTax', (value * schedule.showtime.ticket.child * vat) / (100 + vat));
        setValue(
          'childTotalTax',
          value *
            (((schedule.showtime.ticket.child -
              levy -
              ((schedule.showtime.ticket.child - levy) / (100 + entertainmentTax)) *
                entertainmentTax) /
              (100 + vat)) *
              vat),
        );
        setValue('childConvenienceFee', value * onlineFee);
      } else if (fieldName === 'adults') {
        setValue('adultTicketPriceTotal', value * (schedule.showtime.ticket.adult + onlineFee));
        // setValue(
        //   'adultTotalTax',
        //   value *
        //     ((((schedule.showtime.ticket.adult + levy) * 100 + 2 * levy * entertainmentTax) * vat) /
        //       ((100 + entertainmentTax) * (100 + vat))),
        // );
        // setValue('adultTotalTax', value * ((schedule.showtime.ticket.adult * vat) / (100 + vat)));
        setValue(
          'adultTotalTax',
          value *
            (((schedule.showtime.ticket.adult -
              levy -
              ((schedule.showtime.ticket.adult - levy) / (100 + entertainmentTax)) *
                entertainmentTax) /
              (100 + vat)) *
              vat),
        );
        setValue('adultConvenienceFee', value * onlineFee);
      }
    }

    const adultsTotal = getValues('adultTicketPriceTotal');
    const childTotal = getValues('childTicketPriceTotal');
    const childTotalTax = getValues('childTotalTax');
    const adultTotalTax = getValues('adultTotalTax');

    setTotalVat(childTotalTax + adultTotalTax);
    setTotal(adultsTotal + childTotal);
    setValue('amountPaid', adultsTotal + childTotal);
  };

  useEffect(() => {
    let listener: () => void;
    if (scheduleId) {
      listener = firebase
        .firestore()
        .collection('schedules')
        .doc(scheduleId)
        .onSnapshot((doc) => {
          // const source = doc.metadata.hasPendingWrites ? 'Local' : 'Server';
          // console.log(source, ' data: ', doc.data());
          if (doc.exists) {
            setSchedule({
              ...(doc.data() as Schedule),
              id: doc.id,
            });
            setBookings((doc.data() as Schedule).bookings);
          } else {
            setSchedule(undefined);
            setBookings([]);
            toast('Sorry! Schedule not found', {
              type: 'error',
            });
          }
        });
    }

    return () => {
      if (listener) {
        listener();
      }
    };
  }, [scheduleId]);

  useEffect(() => {
    if (schedule) {
      setValue('childTicketPrice', schedule.showtime.ticket.child);
      setValue('adultTicketPrice', schedule.showtime.ticket.adult);
    }
  }, [schedule, setValue]);

  useEffect(() => {
    setValue('ticketsCount', ticketCount);
  }, [ticketCount, setValue]);

  useEffect(() => {
    setValue('currentSelectionIds', JSON.stringify(currentSelectionIds));
  }, [currentSelectionIds, setValue]);

  return (
    <div>
      <div>
        <div>
          <div className="text-center mb-12 my-4">
            <h3 className="text-white text-2xl mb-2">{schedule?.movie.name}</h3>
            <p className="text-gray-200 text-base">{`${schedule?.cinema.label} - ${format(
              schedule ? parse(schedule.date, dateFormat, new Date()) : new Date(),
              `${dateFormat} - iiii`,
            )} at ${format(
              schedule ? parse(schedule.showtime.time, timeFormat, new Date()) : new Date(),
              timeFormat,
            )}`}</p>
          </div>
        </div>
        <div className="mb-7">
          <div>
            <h3 className={styles.screenText}>SCREEN</h3>
          </div>
        </div>
        <TicketSelect
          seats={bookings}
          cinema={cinema}
          toggleSeatSelect={toggleSelect}
          loading={loading}
        />
        {/* <div className="mx-auto max-w-5xl flex flex-wrap flex-row row gap-y-4 relative">
          {bookings.map((seat) => (
            <div
              className="px-2"
              style={{ flex: `1 0 ${cinema?.seatsPerRow === 15 ? '6.5%' : '10%'}` }}
              key={seat.id}>
              <button
                type="button"
                disabled={loading}
                data-status={seat.status}
                onClick={() => {
                  toggleSelect(seat.id);
                }}
                className={`${styles.seatBtn} ${
                  seat.status === 'available' ? styles.seatAvailable : null
                } ${seat.status === 'selected' ? styles.seatSelected : null} ${
                  seat.status === 'restricted' ? styles.seatRestricted : null
                } ${seat.status === 'temporary-blocked' ? styles.seatTemporaryBlocked : null} ${
                  seat.status === 'unavailable' ? styles.seatUnavailable : null
                }`}>
                {seat.label}
              </button>
            </div>
          ))}
          {loading ? (
            <div className="flex w-full flex-col justify-center h-full absolute bg-opacity-80 bg-black">
              <Loading />
            </div>
          ) : null}
        </div> */}
        <div className="justify-center mt-12 flex">
          <div className="grid grid-cols-3 gap-4 lg:grid-cols-5 justify-between w-full lg:w-3/4">
            <div className="flex text-white flex-col text-center lg:w-auto lg:flex-row">
              <span className={styles.selectedBox} />
              <p className="text-xs lg:text-base">Selected</p>
            </div>
            <div className="flex text-white flex-col text-center lg:w-auto lg:flex-row">
              <span className={styles.availableBox} />
              <p className="text-xs lg:text-base">Available</p>
            </div>
            <div className="flex text-white flex-col text-center lg:w-auto lg:flex-row">
              <span className={styles.restrictedBox} />
              <p className="text-xs lg:text-base">Restricted</p>
            </div>
            <div className="flex text-white flex-col text-center lg:w-auto lg:flex-row">
              <span className={styles.temporaryUnavailableBox} />
              <p className="text-xs lg:text-base">Temporary Unavailable</p>
            </div>
            <div className="flex text-white flex-col text-center lg:w-auto lg:flex-row">
              <span className={styles.unavailableBox} />
              <p className="text-xs lg:text-base">Unavailable</p>
            </div>
          </div>
        </div>
      </div>
      <div>
        <h5 className="text-center mb-12 mt-12 text-white text-lg">
          {ticketCount} ticket(s) selected. Please select attendees
        </h5>
        <form className="w-full">
          <div className="hidden">
            <input type="hidden" {...register('ticketsCount')} />
            <input type="hidden" {...register('currentSelectionIds')} />
            <input type="hidden" {...register('amountPaid')} />
            <input type="hidden" {...register('childTotalTax')} />
            <input type="hidden" {...register('adultTotalTax')} />
          </div>
          <div className="flex flex-row justify-between md:justify-center">
            <div className="form-block w-1/5 md:w-20 md:mr-10 mb-10">
              <label
                htmlFor="adults"
                className="label text-blue-100 text-xs md:text-1xl font-bold block self-center align-middle">
                Adults
              </label>
              <input
                type="number"
                min={0}
                disabled={loading}
                {...restAdultRegister}
                className="form-input w-full md:w-20 mt-1 block border border-gray-200 bg-black rounded-md text-white"
                onChange={async (e) => {
                  if (e.target.value === '') {
                    setValue('adults', '0');
                  }
                  await onAdultChange(e);
                  // setValue('adults', e.target.value);
                  // console.log(getValues());
                  onInputChange(Number.parseInt(e.target.value, 10), 'adults');
                  // setTimeout(() => {
                  //   console.log(getValues());
                  // }, 2000);
                }}
                // onChange={onAdultChange}
              />
            </div>
            <div className="form-block w-1/5 md:w-24 md:mr-10 mb-10">
              <label
                htmlFor="adultTicketPrice"
                className="label text-blue-100 text-xs md:text-1xl font-bold block self-center align-middle">
                Ticket Price
              </label>
              <input
                type="number"
                {...adultTicketPriceRegister}
                readOnly
                className="opacity-45 w-full md:w-24 form-input mt-1 block border border-gray-500 bg-black rounded-md text-gray-300"
              />
            </div>
            <div className="form-block w-1/5 md:w-24 md:mr-10 mb-10">
              <label
                htmlFor="adultConvenienceFee"
                className="text-blue-100 text-xs text-right md:text-left md:text-1xl font-bold block self-center align-middle">
                Online fee
              </label>
              <input
                type="number"
                readOnly
                {...adultConvenienceFeeRegister}
                className="w-full md:w-24 text-center pr-0 opacity-45 form-input mt-1 block border border-gray-500 bg-black rounded-md text-gray-300"
              />
            </div>
            <div className="form-block w-1/5 md:w-24">
              <label
                htmlFor="adultTicketPriceTotal"
                className="text-blue-100 text-xs text-right md:text-left md:text-1xl font-bold block self-center align-middle">
                Subtotal
              </label>
              <input
                type="number"
                {...adultTicketPriceTotalRegister}
                readOnly
                className="w-full md:w-24 text-center pr-0 opacity-45 form-input mt-1 block border border-gray-500 bg-black rounded-md text-gray-300"
              />
            </div>
          </div>
          <div className="flex flex-row justify-between md:justify-center">
            <div className="form-block w-1/4 md:w-20 md:mr-10">
              <label
                htmlFor="children"
                className="text-blue-100 text-xs md:text-1xl font-bold block self-center align-middle">
                Children
              </label>
              <input
                type="number"
                {...restChildrenRegister}
                disabled={loading}
                min={0}
                className="form-input w-full md:w-20 mt-1 block border border-gray-200 bg-black rounded-md text-white"
                onChange={(e) => {
                  if (e.target.value === '') {
                    setValue('children', '0');
                  }
                  onChildrenChange(e);
                  onInputChange(Number.parseInt(e.target.value, 10), 'children');
                }}
              />
            </div>
            <div className="form-block w-1/5 md:w-24 md:mr-10">
              <label
                htmlFor="childTicketPrice"
                className="text-blue-100 text-xs md:text-1xl font-bold block self-center align-middle">
                Ticket Price
              </label>
              <input
                type="number"
                {...childTicketPriceRegister}
                readOnly
                className="opacity-45 w-full md:w-24 form-input mt-1 block border border-gray-500 bg-black rounded-md text-gray-300"
              />
            </div>
            <div className="form-block w-1/5 md:w-24 md:mr-10 mb-10">
              <label
                htmlFor="adultConvenienceFee"
                className="text-blue-100 text-xs text-right md:text-left md:text-1xl font-bold block self-center align-middle">
                Online fee
              </label>
              <input
                type="number"
                readOnly
                {...childConvenienceFeeRegister}
                className="w-full md:w-24 text-center pr-0 opacity-45 form-input mt-1 block border border-gray-500 bg-black rounded-md text-gray-300"
              />
            </div>
            <div className="form-block w-1/5 md:w-24">
              <label
                htmlFor="childTicketPriceTotal"
                className="text-blue-100 text-xs text-right md:text-left md:text-1xl font-bold block self-center align-middle">
                Subtotal
              </label>
              <input
                type="number"
                {...childTicketPriceTotalRegister}
                readOnly
                className="w-full md:w-24 text-center pr-0 opacity-45 form-input mt-1 block border border-gray-500 bg-black rounded-md text-gray-300"
              />
            </div>
          </div>
          <div className="flex flex-col justify-center">
            <div className="flex justify-center mt-7">
              <div>
                <h4 className="text-white text-2xl">
                  Total : {`${currency(total)} (VAT Included : ${currencycents(totalVat)})`}
                </h4>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default forwardRef(MovieTickets);
